/**
 * Reparto de un importe en centavos entre participantes (D4).
 *
 * Algoritmo: **mayor resto**, con desempate determinístico por `sortOrder` y,
 * si aún hay empate, por `participantId`.
 *
 * Garantía: la suma de las partes es EXACTAMENTE igual al total. De esta
 * propiedad depende el invariante I6 (`Σ saldos = 0`).
 */
import { assertCents, roundHalfAwayFromZero, type Cents } from '@/core/money/cents';
import { EconomicsError, type SplitLine } from './types';

/** Escala de los porcentajes: 4 decimales, como `numeric(7,4)`. */
const PERCENT_SCALE = 10_000n;
const FULL = 100n * PERCENT_SCALE; // 1.000.000 = 100 %

export function assertSplitsAreComplete(lines: readonly SplitLine[], label = 'reparto'): void {
  if (lines.length === 0) {
    throw new EconomicsError(`El ${label} no tiene líneas`);
  }

  const seen = new Set<string>();
  let scaledTotal = 0n;

  for (const line of lines) {
    if (seen.has(line.participantId)) {
      throw new EconomicsError(`El ${label} repite al participante ${line.participantId}`);
    }
    seen.add(line.participantId);

    if (!(line.percentage > 0) || line.percentage > 100) {
      throw new EconomicsError(
        `Porcentaje fuera de rango en el ${label}: ${line.percentage} (${line.participantId})`,
      );
    }
    scaledTotal += BigInt(roundHalfAwayFromZero(line.percentage * Number(PERCENT_SCALE)));
  }

  if (scaledTotal !== FULL) {
    const pct = Number(scaledTotal) / Number(PERCENT_SCALE);
    throw new EconomicsError(`El ${label} suma ${pct} % y debe sumar exactamente 100 %`);
  }
}

/**
 * Reparte `totalCents` según `lines`.
 * Soporta totales negativos: el signo se aplica al final, de modo que la suma
 * sigue coincidiendo con el total.
 */
export function allocateCents(totalCents: Cents, lines: readonly SplitLine[]): Map<string, Cents> {
  assertCents(totalCents, 'total a repartir');
  assertSplitsAreComplete(lines);

  const result = new Map<string, Cents>();
  if (totalCents === 0) {
    for (const line of lines) result.set(line.participantId, 0);
    return result;
  }

  const sign = totalCents < 0 ? -1 : 1;
  const absTotal = BigInt(Math.abs(totalCents));

  const parts = lines.map((line) => {
    const scaled = BigInt(roundHalfAwayFromZero(line.percentage * Number(PERCENT_SCALE)));
    const numerator = absTotal * scaled;
    return {
      participantId: line.participantId,
      sortOrder: line.sortOrder,
      base: numerator / FULL,
      remainder: numerator % FULL,
    };
  });

  const distributed = parts.reduce((acc, p) => acc + p.base, 0n);
  let residue = absTotal - distributed;

  // Mayor resto primero; empate por sortOrder y luego por participantId.
  const order = [...parts].sort((a, b) => {
    if (a.remainder !== b.remainder) return a.remainder > b.remainder ? -1 : 1;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.participantId < b.participantId ? -1 : 1;
  });

  const bonus = new Map<string, bigint>();
  for (const p of order) {
    if (residue <= 0n) break;
    bonus.set(p.participantId, 1n);
    residue -= 1n;
  }

  for (const p of parts) {
    const amount = p.base + (bonus.get(p.participantId) ?? 0n);
    const cents = sign * Number(amount);
    // Normaliza -0 a 0: JavaScript distingue ambos y PostgreSQL no. La paridad
    // TS↔SQL exige un único cero (ver parity.db.test.ts).
    result.set(p.participantId, cents === 0 ? 0 : cents);
  }

  return result;
}

/**
 * Construye un reparto en partes iguales que suma EXACTAMENTE 100 %.
 *
 * Con 4 decimales, 100 no siempre es divisible en partes iguales: entre 3
 * participantes cada uno tendría 33,3333 % y la suma daría 99,9999 %. Este
 * helper reparte las milésimas de porciento sobrantes por mayor resto, con el
 * mismo desempate que `allocateCents`: gana el de menor `sortOrder`.
 *
 * Existe para que **el usuario no tenga que resolver el residuo a mano**: la UI
 * llama a esta función y obtiene porcentajes ya válidos.
 */
export function buildEvenSplit(participantIds: readonly string[]): SplitLine[] {
  if (participantIds.length === 0) {
    throw new EconomicsError('No se puede construir un reparto sin participantes');
  }
  if (new Set(participantIds).size !== participantIds.length) {
    throw new EconomicsError('Hay participantes repetidos en el reparto');
  }

  const n = BigInt(participantIds.length);
  const base = FULL / n;
  let residue = FULL - base * n;

  return participantIds.map((participantId, index) => {
    let scaled = base;
    if (residue > 0n) {
      scaled += 1n;
      residue -= 1n;
    }
    return {
      participantId,
      percentage: Number(scaled) / Number(PERCENT_SCALE),
      sortOrder: index + 1,
    };
  });
}
